import React, { FC } from 'react';
import styled from 'styled-components';
import { useHistory } from 'react-router';
import DescriptionIcon from '@material-ui/icons/Description';

type Props = {
  title: string;
  icon?: React.ReactNode;
  iconBg?: string;
  iconColor?: string;
  viewAllTo?: string;
  footer?: React.ReactNode;
  toolbar?: React.ReactNode;
};

const SectionCard: FC<Props> = ({
  title,
  icon = <DescriptionIcon />,
  iconBg,
  iconColor,
  viewAllTo,
  footer,
  toolbar,
  children,
}) => {
  const history = useHistory();
  return (
    <Card>
      <Head>
        <TitleBlock>
          <TitleIcon bg={iconBg} color={iconColor}>
            {icon}
          </TitleIcon>
          <Title>{title}</Title>
        </TitleBlock>
        {toolbar && <ToolbarSlot>{toolbar}</ToolbarSlot>}
        {viewAllTo && (
          <ViewAll
            type="button"
            onClick={() => history.push(viewAllTo)}
          >
            View All
          </ViewAll>
        )}
      </Head>
      <Body>{children}</Body>
      {footer && <Footer>{footer}</Footer>}
    </Card>
  );
};

export default SectionCard;

const Card = styled.div`
  background: var(--neutral-0);
  border: 1px solid var(--border);
  border-radius: 12px;
  box-shadow: var(--shadow-xs);
  display: flex;
  flex-direction: column;
  min-width: 0;
  box-sizing: border-box;
`;

const Head = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  padding: 16px 20px;
  border-bottom: 1px solid var(--border);
  gap: 12px;
`;

const TitleBlock = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

const TitleIcon = styled.span<{
  bg?: string;
  color?: string;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: ${({ bg }) => bg || 'var(--primary-50)'};
  color: ${({ color }) => color || 'var(--primary-600)'};
  flex-shrink: 0;

  svg {
    font-size: 18px;
  }
`;

const Title = styled.div`
  font-size: 1rem;
  font-weight: 700;
  color: var(--mainText);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ViewAll = styled.button`
  flex-shrink: 0;
  background: none;
  border: 1px solid var(--border);
  padding: 6px 12px;
  border-radius: 8px;
  color: var(--primary-600);
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease,
    border-color 0.15s ease;

  &:hover {
    background: var(--neutral-50);
    border-color: var(--neutral-400);
  }
`;

const ToolbarSlot = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  justify-content: flex-end;

  @media only screen and (max-width: 540px) {
    flex-basis: 100%;
    order: 3;
  }
`;

const Body = styled.div`
  min-width: 0;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  border-top: 1px solid var(--border);
  background: var(--neutral-50);
`;
